import React from "react";
import { Helmet } from "react-helmet-async";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  siteBaseUrl,
} from "../constants/publicPageSeo";

/**
 * Per-route document head for SEO and social previews.
 * @param {{ title: string, description: string, path?: string, noindex?: boolean }} props
 */
export default function PageMeta({ title, description, path = "/", noindex = false }) {
  const base = siteBaseUrl();
  const url = `${base}${path === "/" ? "/" : path}`;
  const fullTitle =
    title && !title.includes(SITE_NAME) ? title : title || SITE_NAME;

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: base,
    logo: DEFAULT_OG_IMAGE,
    description,
  };

  const websiteJsonLd =
    path === "/"
      ? {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: base,
          description,
          publisher: { "@type": "Organization", name: SITE_NAME },
        }
      : null;

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:alt" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      {!noindex ? (
        <script type="application/ld+json">
          {JSON.stringify(organizationJsonLd)}
        </script>
      ) : null}
      {!noindex && websiteJsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(websiteJsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
}
